import { useEffect, useState, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { prefetchStartupData } from "@/utils/prefetchCache";
import Navbar from "@/components/Navbar";
import HomePage from "@/pages/HomePage";
import BrandLandingPage from "@/pages/BrandLandingPage";
import HomeLanding from "@/components/landing/HomeLanding";
import CatalogPage from "@/pages/CatalogPage";
import CartPage from "@/pages/CartPage";
import CheckoutPage from "@/pages/CheckoutPage";
import OrderConfirmationPage from "@/pages/OrderConfirmationPage";
import AuthPage from "@/pages/AuthPage";
import ProfilePage from "@/pages/ProfilePage";
import ProtectedRoute from "@/components/ProtectedRoute";
import StaffOrCustomerRoute from "@/components/StaffOrCustomerRoute";
import ErrorBoundary from "@/components/ErrorBoundary";
import PrintStatusPanel from "@/components/PrintStatusPanel";
import CustomerViewToggle from "@/components/CustomerViewToggle";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import FloatingCancelPrintButton from "@/components/FloatingCancelPrintButton";
import AdminFloatingChromeGate from "@/components/AdminFloatingChromeGate";
import { CartProvider } from "@/context/CartContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LiveStockProvider } from "@/context/LiveStockContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { ImageCacheProvider } from "@/context/ImageCacheContext";
import { TimeProvider } from "@/context/TimeContext";
import { SeniorModeProvider } from "@/contexts/SeniorModeContext";
import { SkuSettingsProvider } from "@/contexts/SkuSettingsContext";
import SeniorModeToggle from "@/components/SeniorModeToggle";
import MyOrdersPage from "@/pages/MyOrdersPage";
import CustomerQuotationsPage from "@/pages/CustomerQuotationsPage";
import MyQuotationsPage from "@/pages/MyQuotationsPage";
const NewItemPostPage = lazy(() => import("@/pages/NewItemPostPage"));
const DemoLandingPage = lazy(() => import("@/pages/DemoLandingPage"));
import WhatsAppButton from "@/components/WhatsAppButton";
import FloatingTaskButton from "@/components/FloatingTaskButton";
const TaskManagerPage = lazy(() => import("@/pages/TaskManagerPage"));
import ProfileCompletionModal from "@/components/ProfileCompletionModal";
import OrderNotification from "@/components/OrderNotification";
import CenterOrderAlert from "@/components/CenterOrderAlert";
import PusherSessionTracker from "@/components/PusherSessionTracker";
import StockTrailBell from "@/components/StockTrailBell";
import TallyPendingBell from "@/components/TallyPendingBell";
import TaskPanelBell from "@/components/TaskPanelBell";
// Auto-install axios interceptor that retries any request with a legacy
// part number when the backend returns 404 — see aliasInterceptor.js.
import "@/utils/aliasInterceptor";
const MissingImagesPage = lazy(() => import("@/pages/MissingImagesPage"));
const TaskPanelHub = lazy(() => import("@/pages/TaskPanelHub"));
const RestockPanel = lazy(() => import("@/pages/RestockPanel"));
const CouponAuditPage = lazy(() => import("@/pages/CouponAuditPage"));
const StaffBoxingPage = lazy(() => import("@/pages/StaffBoxingPage"));
const StaffKGLocalTrayPage = lazy(() => import("@/pages/StaffKGLocalTrayPage"));
import LocationGate from "@/components/LocationGate";
import ScrollToTop from "@/components/ScrollToTop";
import "@/App.css";

// Lazy load admin pages for better initial load performance
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const AdminProducts = lazy(() => import("@/pages/AdminProducts"));
const AdminCustomers = lazy(() => import("@/pages/AdminCustomers"));
const AdminOrders = lazy(() => import("@/pages/AdminOrders"));
const AdminBanners = lazy(() => import("@/pages/AdminBanners"));
const AdminLiveUsers = lazy(() => import("@/pages/AdminLiveUsers"));
const AdminImport = lazy(() => import("@/pages/AdminImport"));
const AdminCategories = lazy(() => import("@/pages/AdminCategories"));
const AdminApprovals = lazy(() => import("@/pages/AdminApprovals"));
const TallyPendingPage = lazy(() => import("@/pages/TallyPendingPage"));
const PusherGodModeDashboard = lazy(() => import("@/pages/PusherGodModeDashboard"));
const DataMigrationsPage = lazy(() => import("@/pages/DataMigrationsPage")); // Phase 1A
const WhatsAppHubPage = lazy(() => import("@/pages/WhatsAppHubPage"));
const LiveMarketPulse = lazy(() => import("@/pages/LiveMarketPulse"));

// Task Manager Pages (Lazy loaded)
const TaskManagerDashboard = lazy(() => import("@/pages/TaskManagerDashboard"));
const StockCheckPage = lazy(() => import("@/pages/StockCheckPage"));
const OrderProcessingPage = lazy(() => import("@/pages/OrderProcessingPage"));
const IncomingMaterialPage = lazy(() => import("@/pages/IncomingMaterialPage"));
const TallyPendingBillsPanel = lazy(() => import("@/pages/TallyPendingBillsPanel"));
const VendorApprovalsPanel = lazy(() => import("@/pages/VendorApprovalsPanel"));
const DispatchOrdersPage = lazy(() => import("@/pages/DispatchOrdersPage"));
const WarehouseManagerDashboard = lazy(() => import("@/pages/WarehouseManagerDashboard"));
const WarehouseHeadDashboard = lazy(() => import("@/pages/WarehouseHeadDashboard"));
const OfficeUsersPage = lazy(() => import("@/pages/OfficeUsersPage"));
const PartsLinkingPage = lazy(() => import("@/pages/PartsLinkingPage"));
const AdminPriceRequestsPage = lazy(() => import("@/pages/AdminPriceRequestsPage"));
const AdminPaymentsPage = lazy(() => import("@/pages/AdminPaymentsPage"));
const CustomerLedgerDashboard = lazy(() => import("@/pages/CustomerLedgerDashboard"));
const AdminStickerPage = lazy(() => import("@/pages/Admin/AdminStickerPage"));
const SurveillanceDashboard = lazy(() => import("@/pages/SurveillanceDashboard"));
const EmployeeManagement = lazy(() => import("@/pages/EmployeeManagement"));
const AttendanceManagement = lazy(() => import("@/pages/AttendanceManagement"));
const SalaryManagement = lazy(() => import("@/pages/SalaryManagement"));
const AIMonitoringDashboard = lazy(() => import("@/pages/AIMonitoringDashboard"));
const VideoPlayback = lazy(() => import("@/pages/VideoPlayback"));
const CategorySelection = lazy(() => import("@/pages/CategorySelection"));
const ExportOrdersPage = lazy(() => import("@/pages/ExportOrdersPage"));
const NepalExportPage = lazy(() => import("@/pages/NepalExportPage"));
const DistributorPanel = lazy(() => import("@/pages/DistributorPanel"));
const SalesmanPanel = lazy(() => import("@/pages/SalesmanPanel"));
const SalesmanCatalogue = lazy(() => import("@/pages/SalesmanCatalogue"));
const NTOPage = lazy(() => import("@/pages/NTOPage"));
const VendorPOPage = lazy(() => import("@/pages/VendorPOPage"));
const VendorLeadForm = lazy(() => import("@/pages/VendorLeadForm"));
const VendorDashboard = lazy(() => import("@/pages/VendorDashboard"));
const PerformanceDashboard = lazy(() => import("@/pages/PerformanceDashboard"));
const DocumentSharingPanel = lazy(() => import("@/pages/DocumentSharingPanel"));
const DomesticDistributorPanel = lazy(() => import("@/pages/DomesticDistributorPanel"));
const DistributorOrderProcessing = lazy(() => import("@/pages/DistributorOrderProcessing"));
const PriceMasterPage = lazy(() => import("@/pages/PriceMasterPage"));
const AllProductsPage = lazy(() => import("@/pages/AllProductsPage"));
const WarehouseSetupPage = lazy(() => import("@/pages/WarehouseSetupPage"));
const WarehouseOpsPage = lazy(() => import("@/pages/WarehouseOpsPage"));
const StockTrailHub = lazy(() => import("@/pages/StockTrailHub"));
const NangliOswalStockPanel = lazy(() => import("@/pages/NangliOswalStockPanel"));
const WarehouseAdvancedPage = lazy(() => import("@/pages/WarehouseAdvancedPage"));
const ChequeManagerPage = lazy(() => import("@/pages/ChequeManagerPage"));
const AnalyticsDashboardPage = lazy(() => import("@/pages/AnalyticsDashboardPage"));
const NPGuidedPicking = lazy(() => import("@/pages/NPGuidedPicking"));
const NPPackingVerification = lazy(() => import("@/pages/NPPackingVerification"));
const NPPackingFlow = lazy(() => import("@/pages/NPPackingFlow"));
const NPMrpLabelling = lazy(() => import("@/pages/NPMrpLabelling"));
const NPReboxing = lazy(() => import("@/pages/NPReboxing"));
const PRPackStackScanPage = lazy(() => import("@/pages/PRPackStackScanPage"));
const BulkStackScanPage = lazy(() => import("@/pages/BulkStackScanPage"));
const BulkDrawPickPage = lazy(() => import("@/pages/BulkDrawPickPage"));
const ScanForensicPage = lazy(() => import("@/pages/ScanForensicPage"));
const NPCartonWeights = lazy(() => import("@/pages/NPCartonWeights"));
const NPDispatchHub = lazy(() => import("@/pages/NPDispatchHub"));
const NPIncomingHub = lazy(() => import("@/pages/NPIncomingHub"));
const KGDispatchHub = lazy(() => import("@/pages/KGDispatchHub"));
const StaffLoginPage = lazy(() => import("@/pages/StaffLoginPage"));
const StaffDashboardPage = lazy(() => import("@/pages/StaffDashboardPage"));
const AdminStaffPage = lazy(() => import("@/pages/AdminStaffPage"));
const AdminMyWarehouseTasks = lazy(() => import("@/pages/AdminMyWarehouseTasks"));
const BOMPage = lazy(() => import("@/pages/BOMPage"));
const CompetitorIntelPage = lazy(() => import("@/pages/CompetitorIntelPage"));
const TallyIntegrationPage = lazy(() => import("@/pages/TallyIntegrationPage"));
const MechanicCouponsPage = lazy(() => import("@/pages/MechanicCouponsPage"));
const CouponMasterPage = lazy(() => import("@/pages/CouponMasterPage"));
const Warehouse2Page = lazy(() => import("@/pages/Warehouse2Page"));
const MechanicDashboard = lazy(() => import("@/pages/MechanicDashboard"));
const MechanicChatbotPage = lazy(() => import("@/pages/MechanicChatbotPage"));
const ApiCostTrackerPage = lazy(() => import("@/pages/ApiCostTrackerPage"));
const MarketGapPanel = lazy(() => import("@/pages/MarketGapPanel"));
const MechanicLandingPage = lazy(() => import("@/pages/MechanicLandingPage"));
const ScanDiagnosticsPage = lazy(() => import("@/pages/ScanDiagnosticsPage"));
const ControlTower = lazy(() => import("@/pages/ControlTower"));
const DeadStockExplorerPage = lazy(() => import("@/pages/DeadStockExplorerPage"));
const BomPickingTaskPage = lazy(() => import("@/pages/StaffBomPickFlow"));
// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[60vh]">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-600"></div>
  </div>
);

// Suppress ALL third-party script errors (MSG91, h-captcha, etc.)
if (typeof window !== 'undefined') {
  // Override console.error to suppress third-party errors
  const originalConsoleError = console.error;
  console.error = function(...args) {
    const msg = args.join(' ');
    if (msg.includes('msg91') || msg.includes('Script error') || msg.includes('h-captcha')) {
      return;
    }
    originalConsoleError.apply(console, args);
  };
  
  // Global error handler
  window.onerror = function(message, source, lineno, colno, error) {
    // Suppress third-party errors
    if (source && (source.includes('msg91') || source.includes('verify.msg91') || source.includes('h-captcha'))) {
      return true;
    }
    if (message === 'Script error.' || message === 'Script error' || 
        (message && (message.includes('h-captcha') || message.includes('msg91')))) {
      return true;
    }
    return false;
  };
  
  // Suppress unhandled promise rejections
  window.addEventListener('unhandledrejection', function(event) {
    const reason = String(event.reason || '');
    if (reason.includes('msg91') || reason.includes('Script error') || reason.includes('h-captcha')) {
      event.preventDefault();
      return;
    }
  });
  
  // Suppress error events
  window.addEventListener('error', function(event) {
    if (event.filename && (event.filename.includes('msg91') || event.filename.includes('h-captcha'))) {
      event.preventDefault();
      event.stopPropagation();
      return true;
    }
    if (event.message === 'Script error.' || event.message === 'Script error') {
      event.preventDefault();
      event.stopPropagation();
      return true;
    }
  }, true);
}


// Profile completion check component
const ProfileCompletionCheck = () => {
  const { user, token, updateUser } = useAuth();
  const [showModal, setShowModal] = useState(false);
  
  // Check if user is from India (non-export)
  const isIndianCustomer = user && (user.country_code === '91' || !user.country_code);
  
  // Check if user is from Delhi (transport/courier not mandatory for Delhi)
  const isDelhiCustomer = user && user.state?.toLowerCase() === 'delhi';
  
  // Check if GST/Private Marka details are missing
  const needsGstDetails = isIndianCustomer && !user.gst_number && !user.is_urp;
  const needsPrivateMarka = user && !user.private_marka;
  
  // Check if Transport/Courier details are missing (mandatory for non-Delhi Indian customers)
  const needsTransportDetails = isIndianCustomer && !isDelhiCustomer && !user.preferred_transporter;
  const needsCourierDetails = isIndianCustomer && !isDelhiCustomer && !user.preferred_courier;
  
  useEffect(() => {
    // Show modal if user is logged in and:
    // 1. Profile is incomplete (profile_complete flag), OR
    // 2. Quick-registered user (profile_incomplete flag), OR
    // 3. Missing GST details (for Indian customers), OR
    // 4. Missing Private Marka, OR
    // 5. Missing Transport/Courier (for non-Delhi Indian customers)
    if (user && token && (
      user.profile_complete === false || 
      user.profile_incomplete === true ||
      needsGstDetails ||
      needsPrivateMarka ||
      needsTransportDetails ||
      needsCourierDetails
    )) {
      setShowModal(true);
    }
  }, [user, token, needsGstDetails, needsPrivateMarka, needsTransportDetails, needsCourierDetails]);
  
  if (!showModal || !user) return null;
  
  // Determine if profile completion is required (blocking)
  const isRequired = user.profile_incomplete === true || needsGstDetails || needsPrivateMarka || needsTransportDetails || needsCourierDetails;
  
  return (
    <ProfileCompletionModal
      isOpen={showModal}
      onClose={() => {
        // Only allow closing if profile is complete and has all required fields
        if (isRequired) {
          return; // Don't allow closing
        }
        setShowModal(false);
      }}
      customer={user}
      token={token}
      onProfileUpdated={(updatedCustomer) => {
        updateUser(updatedCustomer);
        setShowModal(false);
      }}
      required={isRequired}
    />
  );
};

function App() {
  // Prefetch slow-changing data on app start (brands, rack structure)
  useEffect(() => { prefetchStartupData(); }, []);

  // ──────────────────────────────────────────────────────────────
  // Phase-1 perf (2026-05-29): auto-tag every <img> without an
  // explicit `loading` attribute as `loading="lazy"` + `decoding="async"`.
  // Saves a huge amount of initial-paint bandwidth on image-heavy
  // pages (part catalogue, NTO panel, vendor cards) because the
  // browser now defers images that aren't on screen yet. Zero JSX
  // changes — MutationObserver catches every <img> the React tree
  // mounts, including ones added later (modal opens, scroll loads).
  // Risk: zero. Images already in <input>/avatar/QR/lightbox flows
  // can opt out by setting loading="eager" explicitly in JSX.
  // ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const tagImg = (img) => {
      if (!img.hasAttribute('loading')) img.setAttribute('loading', 'lazy');
      if (!img.hasAttribute('decoding')) img.setAttribute('decoding', 'async');
    };
    document.querySelectorAll('img').forEach(tagImg);
    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const n of m.addedNodes) {
          if (!(n instanceof Element)) continue;
          if (n.tagName === 'IMG') tagImg(n);
          n.querySelectorAll && n.querySelectorAll('img').forEach(tagImg);
        }
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });
    return () => mo.disconnect();
  }, []);

  // Modal-overlay immunity for notification cards.
  //
  // Radix `<Dialog modal>` (used by every shadcn modal) walks the DOM
  // and applies `inert` + `aria-hidden="true"` to every sibling of its
  // portal so screen readers and pointer events stay inside the
  // dialog. The side-effect: the bottom-left "Order Received" toast
  // and the new center-screen "Order in Processing" alert become
  // un-clickable until the modal is dismissed.
  //
  // Fix: any element marked `data-overlay-immune="true"` (and all its
  // descendants) gets those attributes stripped continuously, so they
  // remain interactive even when a modal is open behind them. The
  // observer also reaches into Radix's own overlay siblings so toasts
  // / alerts can be closed without first closing the underlying
  // modal — which was exactly the bug the user reported.
  useEffect(() => {
    const stripImmunity = (node) => {
      if (!(node instanceof Element)) return;
      if (node.hasAttribute('data-overlay-immune')) {
        if (node.hasAttribute('inert')) node.removeAttribute('inert');
        if (node.getAttribute('aria-hidden') === 'true') {
          node.setAttribute('aria-hidden', 'false');
        }
      }
    };
    const sweep = () => {
      document.querySelectorAll('[data-overlay-immune="true"]').forEach(stripImmunity);
    };
    sweep();
    const mo = new MutationObserver((records) => {
      let needsSweep = false;
      for (const r of records) {
        if (r.type === 'attributes' &&
            (r.attributeName === 'inert' || r.attributeName === 'aria-hidden')) {
          needsSweep = true;
          break;
        }
        if (r.addedNodes && r.addedNodes.length) {
          needsSweep = true;
          break;
        }
      }
      if (needsSweep) sweep();
    });
    mo.observe(document.body, {
      attributes: true,
      attributeFilter: ['inert', 'aria-hidden'],
      childList: true,
      subtree: true,
    });

    // ──────────────────────────────────────────────────────────────
    // Stop pointer events originating inside `data-overlay-immune`
    // elements from being interpreted as "outside" clicks by Radix
    // Dialog / Popover etc. Without this, clicking the Dismiss button
    // on the center-screen Order Alert (which renders in its OWN
    // portal at z-index 100000) caused Radix's DismissableLayer to
    // close the user's underlying modal (e.g. the Map Item dialog),
    // losing their in-progress form state.
    //
    // We register CAPTURE-phase listeners on `document` BEFORE any
    // Radix dialog mounts (Radix attaches its own document listeners
    // only when a dialog opens). Listener registration order
    // determines firing order within the same phase + same element,
    // so ours runs first and stops the event from reaching Radix's
    // outside-click detector via stopImmediatePropagation().
    // ──────────────────────────────────────────────────────────────
    const stopIfInImmune = (e) => {
      const t = e.target;
      if (t && t.closest && t.closest('[data-overlay-immune="true"]')) {
        e.stopImmediatePropagation();
      }
    };
    const opts = { capture: true };
    document.addEventListener('pointerdown', stopIfInImmune, opts);
    document.addEventListener('mousedown', stopIfInImmune, opts);
    document.addEventListener('touchstart', stopIfInImmune, opts);

    return () => {
      mo.disconnect();
      document.removeEventListener('pointerdown', stopIfInImmune, opts);
      document.removeEventListener('mousedown', stopIfInImmune, opts);
      document.removeEventListener('touchstart', stopIfInImmune, opts);
    };
  }, []);
  
  // Image protection - prevent copying, screenshots, and keyboard shortcuts
  useEffect(() => {
    // Disable right-click context menu on images
    const handleContextMenu = (e) => {
      if (e.target.tagName === 'IMG' && e.target.dataset.protected === 'true') {
        e.preventDefault();
        return false;
      }
    };

    // Disable keyboard shortcuts for screenshots and copying
    const handleKeyDown = (e) => {
      // Block Print Screen
      if (e.key === 'PrintScreen') {
        e.preventDefault();
        return false;
      }
      // Block Ctrl+P (Print)
      if (e.ctrlKey && e.key === 'p') {
        e.preventDefault();
        return false;
      }
      // Block Ctrl+S (Save)
      if (e.ctrlKey && e.key === 's') {
        e.preventDefault();
        return false;
      }
      // Block Ctrl+Shift+I (DevTools)
      if (e.ctrlKey && e.shiftKey && e.key === 'I') {
        e.preventDefault();
        return false;
      }
      // Block F12 (DevTools)
      if (e.key === 'F12') {
        e.preventDefault();
        return false;
      }
    };

    // Disable drag on images
    const handleDragStart = (e) => {
      if (e.target.tagName === 'IMG') {
        e.preventDefault();
        return false;
      }
    };

    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('dragstart', handleDragStart);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('dragstart', handleDragStart);
    };
  }, []);

  return (
    <ErrorBoundary>
      <LocationGate>
        <AuthProvider>
          <ThemeProvider>
          <SkuSettingsProvider>
          <SeniorModeProvider>
          <TimeProvider>
            <ImageCacheProvider>
              <CartProvider>
                <LiveStockProvider>
                <BrowserRouter>
                  <ScrollToTop />
                  <div className="App min-h-screen">
                    <Navbar />
                    <CustomerViewToggle />
                    {/* ImpersonateCustomerSwitcher merged into CustomerViewToggle (2026-06-05) */}
                    <ImpersonationBanner />
                    <Routes>
                    <Route path="/auth" element={<AuthPage />} />
                    <Route path="/demo" element={<Suspense fallback={<PageLoader />}><DemoLandingPage /></Suspense>} />
                    <Route path="/staff-login" element={<Suspense fallback={<PageLoader />}><StaffLoginPage /></Suspense>} />
                    <Route path="/staff" element={<Suspense fallback={<PageLoader />}><StaffDashboardPage /></Suspense>} />
                    <Route path="/staff/missing-images" element={<Suspense fallback={<PageLoader />}><MissingImagesPage /></Suspense>} />
                    <Route path="/staff/task-panel" element={<Suspense fallback={<PageLoader />}><TaskPanelHub /></Suspense>} />
                    <Route path="/staff/restock" element={<Suspense fallback={<PageLoader />}><RestockPanel /></Suspense>} />
                    <Route path="/staff/coupon-audit" element={<Suspense fallback={<PageLoader />}><CouponAuditPage /></Suspense>} />
                    <Route path="/staff/boxing" element={<Suspense fallback={<PageLoader />}><StaffBoxingPage /></Suspense>} />
                    <Route path="/staff/picking/:orderId" element={<Suspense fallback={<PageLoader />}><NPGuidedPicking /></Suspense>} />
                    <Route path="/select-category" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><CategorySelection /></Suspense></ProtectedRoute>} />
                    <Route path="/" element={<ProtectedRoute><HomeLanding /></ProtectedRoute>} />
                    <Route path="/categories" element={<ProtectedRoute><BrandLandingPage /></ProtectedRoute>} />
                    <Route path="/search/:brand" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
                    <Route path="/catalog" element={<ProtectedRoute><CatalogPage /></ProtectedRoute>} />
                    <Route path="/cart" element={<ProtectedRoute><CartPage /></ProtectedRoute>} />
                    <Route path="/checkout" element={<ProtectedRoute><CheckoutPage /></ProtectedRoute>} />
                    <Route path="/order-confirmation/:orderId" element={<ProtectedRoute><OrderConfirmationPage /></ProtectedRoute>} />
                    <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
                    <Route path="/my-orders" element={<ProtectedRoute><MyOrdersPage /></ProtectedRoute>} />
                    <Route path="/customer-quotations" element={<ProtectedRoute><CustomerQuotationsPage /></ProtectedRoute>} />
                    <Route path="/my-quotations" element={<ProtectedRoute><MyQuotationsPage /></ProtectedRoute>} />
                    <Route path="/posters/new" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><NewItemPostPage /></Suspense></ProtectedRoute>} />
                    <Route path="/tally-pending" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><TallyPendingPage /></Suspense></ProtectedRoute>} />
                    <Route path="/tasks" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><TaskManagerPage /></Suspense></ProtectedRoute>} />
                    
                    {/* Admin Routes - Lazy loaded */}
                    <Route path="/admin" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><AdminDashboard /></Suspense></ProtectedRoute>} />
                    <Route path="/admin/approvals" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><AdminApprovals /></Suspense></ProtectedRoute>} />
                    <Route path="/admin/products" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><AdminProducts /></Suspense></ProtectedRoute>} />
                    <Route path="/admin/customers" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><AdminCustomers /></Suspense></ProtectedRoute>} />
                    <Route path="/admin/orders" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><AdminOrders /></Suspense></ProtectedRoute>} />
                    <Route path="/admin/banners" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><AdminBanners /></Suspense></ProtectedRoute>} />
                    <Route path="/admin/live-users" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><AdminLiveUsers /></Suspense></ProtectedRoute>} />
                  <Route path="/admin/import" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><AdminImport /></Suspense></ProtectedRoute>} />
                  <Route path="/admin/categories" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><AdminCategories /></Suspense></ProtectedRoute>} />
                  <Route path="/admin/god-mode" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><PusherGodModeDashboard /></Suspense></ProtectedRoute>} />
                  <Route path="/admin/data-migrations" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><DataMigrationsPage /></Suspense></ProtectedRoute>} />
                  <Route path="/admin/whatsapp-hub" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><WhatsAppHubPage /></Suspense></ProtectedRoute>} />
                  <Route path="/admin/market-pulse" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><LiveMarketPulse /></Suspense></ProtectedRoute>} />
                  <Route path="/admin/stickers" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><AdminStickerPage /></Suspense></ProtectedRoute>} />
                  
                  {/* Task Manager Routes - Lazy loaded */}
                  <Route path="/task-manager" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><TaskManagerDashboard /></Suspense></ProtectedRoute>} />
                  <Route path="/bom-picking/:woId" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><BomPickingTaskPage /></Suspense></ProtectedRoute>} />
                  <Route path="/task-manager/stock-check" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><StockCheckPage /></Suspense></ProtectedRoute>} />
                  <Route path="/task-manager/order/:orderId" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><OrderProcessingPage /></Suspense></ProtectedRoute>} />
                  <Route path="/task-manager/incoming" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><IncomingMaterialPage /></Suspense></ProtectedRoute>} />
                  <Route path="/task-manager/incoming/edit/:materialId" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><IncomingMaterialPage /></Suspense></ProtectedRoute>} />
                  <Route path="/task-manager/tally-pending-bills" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><TallyPendingBillsPanel /></Suspense></ProtectedRoute>} />
                  <Route path="/admin/vendor-approvals" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><VendorApprovalsPanel /></Suspense></ProtectedRoute>} />
                  <Route path="/task-manager/dispatch" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><DispatchOrdersPage /></Suspense></ProtectedRoute>} />
                  <Route path="/task-manager/parts-linking" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><PartsLinkingPage /></Suspense></ProtectedRoute>} />
                  
                  {/* NP Warehouse Picking & Packing */}
                  <Route path="/task-manager/np-picking/:orderId" element={<StaffOrCustomerRoute><Suspense fallback={<PageLoader />}><NPGuidedPicking /></Suspense></StaffOrCustomerRoute>} />
                  <Route path="/task-manager/np-packing/:orderId" element={<StaffOrCustomerRoute><Suspense fallback={<PageLoader />}><NPPackingFlow /></Suspense></StaffOrCustomerRoute>} />
                  {/* Legacy packing screen (manual qty entry) — kept for reference only. */}
                  <Route path="/task-manager/np-packing-legacy/:orderId" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><NPPackingVerification /></Suspense></ProtectedRoute>} />
                  <Route path="/task-manager/np-mrp-label/:orderId" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><NPMrpLabelling /></Suspense></ProtectedRoute>} />
                  <Route path="/task-manager/np-carton-weights/:orderId" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><NPCartonWeights /></Suspense></ProtectedRoute>} />
                  <Route path="/staff/mrp-label/:orderId" element={<StaffOrCustomerRoute><Suspense fallback={<PageLoader />}><NPMrpLabelling /></Suspense></StaffOrCustomerRoute>} />
                  {/* RC-129 · NP Reboxing (post-MRP, pre-Packing) */}
                  <Route path="/np-reboxing/:planId" element={<StaffOrCustomerRoute><Suspense fallback={<PageLoader />}><NPReboxing /></Suspense></StaffOrCustomerRoute>} />
                  <Route path="/staff/np-reboxing/:planId" element={<StaffOrCustomerRoute><Suspense fallback={<PageLoader />}><NPReboxing /></Suspense></StaffOrCustomerRoute>} />
                  {/* 2026-07-20 · Full-screen PR packing / stacking scan panels (replaces the old modal) */}
                  <Route path="/staff/pack-scan/:taskId" element={<StaffOrCustomerRoute><Suspense fallback={<PageLoader />}><PRPackStackScanPage kind="pack" /></Suspense></StaffOrCustomerRoute>} />
                  <Route path="/staff/stack-scan/:taskId" element={<StaffOrCustomerRoute><Suspense fallback={<PageLoader />}><PRPackStackScanPage kind="stack" /></Suspense></StaffOrCustomerRoute>} />
                  <Route path="/staff/bulk-stack-scan/:taskId" element={<StaffOrCustomerRoute><Suspense fallback={<PageLoader />}><BulkStackScanPage /></Suspense></StaffOrCustomerRoute>} />
                  <Route path="/staff/bulk-draw-pick/:taskId" element={<StaffOrCustomerRoute><Suspense fallback={<PageLoader />}><BulkDrawPickPage /></Suspense></StaffOrCustomerRoute>} />
                  <Route path="/audit/scan-forensic" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><ScanForensicPage /></Suspense></ProtectedRoute>} />
                  <Route path="/staff/carton-weights/:orderId" element={<StaffOrCustomerRoute><Suspense fallback={<PageLoader />}><NPCartonWeights /></Suspense></StaffOrCustomerRoute>} />
                  {/* KG Local Dispatch — Staff Panel tray-scan */}
                  <Route path="/staff/kg-local-tray/:orderId" element={<StaffOrCustomerRoute><Suspense fallback={<PageLoader />}><StaffKGLocalTrayPage /></Suspense></StaffOrCustomerRoute>} />
                  <Route path="/task-manager/np-dispatch" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><NPDispatchHub /></Suspense></ProtectedRoute>} />
                  <Route path="/task-manager/np-incoming" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><NPIncomingHub /></Suspense></ProtectedRoute>} />
                  <Route path="/task-manager/kg-dispatch" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><KGDispatchHub /></Suspense></ProtectedRoute>} />
                  <Route path="/admin/kg-dispatch-hub" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><KGDispatchHub /></Suspense></ProtectedRoute>} />
                  
                  {/* Admin Price Requests */}
                  <Route path="/admin/price-requests" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><AdminPriceRequestsPage /></Suspense></ProtectedRoute>} />
                  
                  {/* Admin Payments */}
                  <Route path="/admin/payments" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><AdminPaymentsPage /></Suspense></ProtectedRoute>} />
                  
                  {/* Customer Ledger & WhatsApp Reminder Dashboard */}
                  <Route path="/admin/ledger" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><CustomerLedgerDashboard /></Suspense></ProtectedRoute>} />
                  
                  {/* AI Surveillance & HR Dashboard */}
                  <Route path="/admin/surveillance" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><SurveillanceDashboard /></Suspense></ProtectedRoute>} />
                  <Route path="/admin/surveillance/employees" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><EmployeeManagement /></Suspense></ProtectedRoute>} />
                  <Route path="/admin/surveillance/attendance" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><AttendanceManagement /></Suspense></ProtectedRoute>} />
                  <Route path="/admin/surveillance/salary" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><SalaryManagement /></Suspense></ProtectedRoute>} />
                  <Route path="/admin/surveillance/monitoring" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><AIMonitoringDashboard /></Suspense></ProtectedRoute>} />
                  <Route path="/admin/surveillance/playback" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><VideoPlayback /></Suspense></ProtectedRoute>} />
                  <Route path="/admin/performance" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><PerformanceDashboard /></Suspense></ProtectedRoute>} />
                  
                  {/* Warehouse Manager Dashboard */}
                  <Route path="/warehouse" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><WarehouseManagerDashboard /></Suspense></ProtectedRoute>} />
                  
                  {/* Warehouse Head Dashboard */}
                  <Route path="/warehouse-head" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><WarehouseHeadDashboard /></Suspense></ProtectedRoute>} />
                  
                  {/* Office Users Management */}
                  <Route path="/admin/office-users" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><OfficeUsersPage /></Suspense></ProtectedRoute>} />
                  <Route path="/admin/staff" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><AdminStaffPage /></Suspense></ProtectedRoute>} />
                  <Route path="/admin/my-warehouse-tasks" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><AdminMyWarehouseTasks /></Suspense></ProtectedRoute>} />
                  
                  {/* BOM / Assembly */}
                  <Route path="/admin/bom" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><BOMPage /></Suspense></ProtectedRoute>} />
                  
                  {/* Export Orders */}
                  <Route path="/admin/export-orders" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><ExportOrdersPage /></Suspense></ProtectedRoute>} />
                  <Route path="/admin/nepal-orders" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><NepalExportPage /></Suspense></ProtectedRoute>} />
                  
                  {/* Distributor Panel - Nepal Distributor */}
                  <Route path="/distributor" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><DistributorPanel /></Suspense></ProtectedRoute>} />
                  {/* Domestic Distributor Panel - role=distributor or admin */}
                  <Route path="/dist" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><DomesticDistributorPanel /></Suspense></ProtectedRoute>} />
                  <Route path="/dist/order/:orderId" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><DistributorOrderProcessing /></Suspense></ProtectedRoute>} />
                  <Route path="/salesman" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><SalesmanPanel /></Suspense></ProtectedRoute>} />
                  <Route path="/salesman/catalogue" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><SalesmanCatalogue /></Suspense></ProtectedRoute>} />
                  
                  {/* NTO Panel - Admin Only */}
                  <Route path="/nto" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><NTOPage /></Suspense></ProtectedRoute>} />
                  
                  {/* Vendor Magic Link - Public */}
                  <Route path="/vendor-po/:token" element={<Suspense fallback={<PageLoader />}><VendorPOPage /></Suspense>} />
                  
                  {/* Vendor Lead Form - Public */}
                  <Route path="/vendor-register" element={<Suspense fallback={<PageLoader />}><VendorLeadForm /></Suspense>} />
                  <Route path="/vendor-dashboard" element={<Suspense fallback={<PageLoader />}><VendorDashboard /></Suspense>} />
                  
                  {/* Document Sharing Panel - Admin Only */}
                  <Route path="/doc-sharing" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><DocumentSharingPanel /></Suspense></ProtectedRoute>} />
                  
                  {/* Price Master Panel - Restricted Admins Only */}
                  <Route path="/price-master" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><PriceMasterPage /></Suspense></ProtectedRoute>} />

                  {/* All Products / Dead Stock Panel - Admin Only */}
                  <Route path="/admin/all-products" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><AllProductsPage /></Suspense></ProtectedRoute>} />
                  
                  {/* Warehouse Management - Admin Only */}
                  <Route path="/warehouse-setup" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><WarehouseSetupPage /></Suspense></ProtectedRoute>} />
                  
                  {/* Warehouse Operations - Admin Only */}
                  <Route path="/warehouse-ops" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><WarehouseOpsPage /></Suspense></ProtectedRoute>} />

                  {/* Stock Trail Hub — Master Admin Only (9999313175) */}
                  <Route path="/warehouse/stock-trail/*" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><StockTrailHub /></Suspense></ProtectedRoute>} />
                  {/* Oswal-owned stock at Nangli — convert (bill) to Jai Paras */}
                  <Route path="/warehouse/oswal-stock" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><NangliOswalStockPanel /></Suspense></ProtectedRoute>} />
                  
                  {/* Warehouse Intelligence - Admin Only */}
                  <Route path="/warehouse-intel" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><WarehouseAdvancedPage /></Suspense></ProtectedRoute>} />
                  <Route path="/cheque-manager" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><ChequeManagerPage /></Suspense></ProtectedRoute>} />
                  <Route path="/analytics" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><AnalyticsDashboardPage /></Suspense></ProtectedRoute>} />
                  <Route path="/competitor-intel" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><CompetitorIntelPage /></Suspense></ProtectedRoute>} />
                  <Route path="/tally-integration" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><TallyIntegrationPage /></Suspense></ProtectedRoute>} />
                  <Route path="/admin/mechanic-coupons" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><MechanicCouponsPage /></Suspense></ProtectedRoute>} />
                  <Route path="/admin/coupon-master" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><CouponMasterPage /></Suspense></ProtectedRoute>} />
                  <Route path="/admin/warehouse-2" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><Warehouse2Page /></Suspense></ProtectedRoute>} />
                  <Route path="/mechanic-dashboard" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><MechanicDashboard /></Suspense></ProtectedRoute>} />
                  <Route path="/admin/api-costs" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><ApiCostTrackerPage /></Suspense></ProtectedRoute>} />
                  <Route path="/admin/market-gap" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><MarketGapPanel /></Suspense></ProtectedRoute>} />
                  <Route path="/mechanic" element={<Suspense fallback={<PageLoader />}><MechanicChatbotPage /></Suspense>} />
                  {/* Physical coupon QR landing — /c/:code redirects into /mechanic?code=… */}
                  <Route path="/c/:code" element={<Suspense fallback={<PageLoader />}><MechanicLandingPage /></Suspense>} />
                  {/* Scan Diagnostics — admin tool to debug rejected QR scans */}
                  <Route path="/admin/scan-diagnostics" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><ScanDiagnosticsPage /></Suspense></ProtectedRoute>} />
                  {/* Control Tower — single-pane-of-glass system diagnostics */}
                  <Route path="/admin/control-tower" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><ControlTower /></Suspense></ProtectedRoute>} />
                  {/* Dead-Stock Explorer — unsold inventory with competitor MRP sync */}
                  <Route path="/admin/dead-stock" element={<ProtectedRoute><Suspense fallback={<PageLoader />}><DeadStockExplorerPage /></Suspense></ProtectedRoute>} />
                </Routes>
                <Toaster />
                <WhatsAppButton />
                <ProfileCompletionCheck />
                <PusherSessionTracker />
                {/* ── Admin-only floating chrome — all hide together when
                    admin clicks "View as Customer" so the screen matches
                    what a customer actually sees. ───────────────────── */}
                <AdminFloatingChromeGate>
                  <OrderNotification />
                  <CenterOrderAlert />
                  <PrintStatusPanel />
                  <SeniorModeToggle />
                  <StockTrailBell />
                  <TallyPendingBell />
                  <TaskPanelBell />
                  <FloatingTaskButton />
                  <FloatingCancelPrintButton />
                </AdminFloatingChromeGate>
              </div>
            </BrowserRouter>
                </LiveStockProvider>
          </CartProvider>
        </ImageCacheProvider>
      </TimeProvider>
      </SeniorModeProvider>
      </SkuSettingsProvider>
      </ThemeProvider>
      </AuthProvider>
    </LocationGate>
    </ErrorBoundary>
  );
}

export default App;
